// The CMS blood-pressure rule: a filing needs one local day in the last 15 with 3+ paired cuff readings,
// and sends 3 readings from that day. Spliced into WF37, WF9, WF10 and the companion by
// scripts/sync-bp-session.mjs; the app imports it through app/src/logic/bpSession.ts. Pure: no import,
// no Node built-in, no clock (callers pass nowMs). Spec: docs/specs/2026-10-30-bp-three-readings-same-day.md

// BP-SESSION-START
const BP_SESSION_WINDOW_DAYS = 15;
const BP_SESSION_MIN_READINGS = 3;
const BP_SESSION_DEFAULT_TZ = 'America/New_York';
// Withings attrib 2 = typed for this user, 4 = typed at account creation; RFA Appendix C p55 bars both.
const BP_SESSION_MANUAL_ATTRIB = [2, 4];
const BP_SESSION_RULE_BASELINE = 'highest_day_3_systolic_in_15d';
const BP_SESSION_RULE_LATEST = 'latest_day_3_in_15d';

function bpTimeZone(tz) {
  if (typeof tz !== 'string' || !tz.trim()) return BP_SESSION_DEFAULT_TZ;
  try { new Intl.DateTimeFormat('en-US', { timeZone: tz.trim() }); return tz.trim(); } catch (e) { return BP_SESSION_DEFAULT_TZ; }
}

// formatToParts with explicit fields: a locale's default pattern differs between ICU builds (n8n vs browsers).
function bpLocalClock(tz) {
  let fmt = null;
  try {
    fmt = new Intl.DateTimeFormat('en-US', { timeZone: bpTimeZone(tz), year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  } catch (e) { fmt = null; }
  return function (ms) {
    if (!fmt) { const iso = new Date(ms).toISOString(); return { day: iso.slice(0, 10), time: iso.slice(11, 16) }; }
    const p = {};
    for (const part of fmt.formatToParts(new Date(ms))) p[part.type] = part.value;
    return { day: p.year + '-' + p.month + '-' + p.day, time: (p.hour === '24' ? '00' : p.hour) + ':' + p.minute };
  };
}

function bpIsManualAttrib(attrib) { return BP_SESSION_MANUAL_ATTRIB.indexOf(attrib) !== -1; }

// WF4 rows ({type, value, date, grpid, attrib}) -> one reading per cuff panel. Paired by grpid (the group date is
// second-resolution, so two panels can share it), by date when grpid is absent. A 0 is not a reading; a panel
// missing either component is invalid, not partial; a duplicate type inside one group keeps the first value.
function bpPairsFromMeasures(measures) {
  const byReading = new Map();
  for (const m of Array.isArray(measures) ? measures : []) {
    if (!m) continue;
    const t = Number(m.type);
    if (t !== 10 && t !== 9) continue;
    const at = String(m.date || '');
    const v = Number(m.value);
    if (!at || !Number.isFinite(v) || v <= 0) continue;
    const key = (m.grpid === undefined || m.grpid === null) ? 'd:' + at : 'g:' + String(m.grpid);
    const slot = byReading.get(key) || { at };
    const field = t === 10 ? 'sys' : 'dia';
    if (slot[field] === undefined) slot[field] = Math.round(v * 100) / 100;
    if (slot.attrib === undefined) slot.attrib = (m.attrib === undefined || m.attrib === null) ? null : Number(m.attrib);
    byReading.set(key, slot);
  }
  const out = [];
  for (const s of byReading.values()) {
    const ms = Date.parse(s.at);
    if (!Number.isFinite(ms) || !Number.isFinite(s.sys) || !Number.isFinite(s.dia)) continue;
    const attrib = s.attrib === undefined ? null : s.attrib;
    out.push({ at: s.at, ms, sys: s.sys, dia: s.dia, attrib, manual: bpIsManualAttrib(attrib) });
  }
  return out.sort((a, b) => a.ms - b.ms);
}

// readings: [{ms, sys, dia, manual?, at?, attrib?}]. Baseline: the qualifying day whose 3 highest systolic have the
// highest mean (tie -> later day), those 3. Otherwise: the latest qualifying day, its latest 3. No day qualifies:
// the best day (most readings, tie -> later) so the preview can say "2 of 3 on Oct 28".
function bpSessionWindow(readings, opts) {
  const o = opts || {};
  const nowMs = Number(o.nowMs);
  const baseline = String(o.reportKind || '') === 'baseline';
  const zone = bpTimeZone(o.timeZone);
  const allowManual = o.allowManual === true;
  const clock = bpLocalClock(zone);
  const cutoff = nowMs - BP_SESSION_WINDOW_DAYS * 86400000;
  const inWindow = (Array.isArray(readings) ? readings : [])
    .filter((r) => r && Number.isFinite(r.ms) && Number.isFinite(r.sys) && Number.isFinite(r.dia) && r.sys > 0 && r.dia > 0 && r.ms >= cutoff)
    .slice().sort((a, b) => a.ms - b.ms);
  const manualInWindow = inWindow.filter((r) => r.manual === true).length;
  const groupByDay = (list) => {
    const days = new Map();
    for (const r of list) {
      const c = clock(r.ms);
      const row = { at: r.at || new Date(r.ms).toISOString(), ms: r.ms, sys: r.sys, dia: r.dia, attrib: r.attrib === undefined ? null : r.attrib, manual: r.manual === true, local_day: c.day, local_time: c.time };
      if (!days.has(c.day)) days.set(c.day, []);
      days.get(c.day).push(row);
    }
    return Array.from(days.entries()).map(([day, rows]) => ({ day, rows })).sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0));
  };
  const dayList = groupByDay(allowManual ? inWindow : inWindow.filter((r) => r.manual !== true));
  const qualifying = dayList.filter((d) => d.rows.length >= BP_SESSION_MIN_READINGS);
  const byTime = (a, b) => a.ms - b.ms;
  let day = null;
  let chosen = [];
  if (qualifying.length) {
    if (baseline) {
      let best = null;
      for (const d of qualifying) {
        const top = d.rows.slice().sort((a, b) => (b.sys - a.sys) || (b.ms - a.ms)).slice(0, BP_SESSION_MIN_READINGS);
        const mean = top.reduce((s, r) => s + r.sys, 0) / BP_SESSION_MIN_READINGS;
        if (!best || mean > best.mean || (mean === best.mean && d.day > best.day)) best = { day: d.day, mean, top };
      }
      day = best.day;
      chosen = best.top.slice().sort(byTime);
    } else {
      const last = qualifying[qualifying.length - 1];
      day = last.day;
      chosen = last.rows.slice(-BP_SESSION_MIN_READINGS);
    }
  } else if (dayList.length) {
    let best = dayList[0];
    for (const d of dayList) if (d.rows.length >= best.rows.length) best = d;
    day = best.day;
    chosen = best.rows.slice();
  }
  // Typed readings are what kept this patient short only if counting them would have produced a qualifying day.
  const manualBlocking = !allowManual && !qualifying.length && manualInWindow > 0
    && groupByDay(inWindow).some((d) => d.rows.length >= BP_SESSION_MIN_READINGS);
  return {
    ok: qualifying.length > 0,
    readings: chosen,
    day,
    time_zone: zone,
    found: chosen.length,
    in_window: dayList.reduce((s, d) => s + d.rows.length, 0),
    qualifying_days: qualifying.length,
    manual_excluded: allowManual ? 0 : manualInWindow,
    manual_in_window: manualInWindow,
    manual_blocking: manualBlocking,
    sent_manual: chosen.filter((r) => r.manual === true).length,
    selection_rule: baseline ? BP_SESSION_RULE_BASELINE : BP_SESSION_RULE_LATEST,
    min_required: BP_SESSION_MIN_READINGS,
    window_days: BP_SESSION_WINDOW_DAYS,
  };
}

// A patient ask closes on a qualifying day taken after it appeared: older readings answered an earlier ask.
function bpSessionSince(readings, sinceMs, opts) {
  const since = Number(sinceMs);
  const after = (Array.isArray(readings) ? readings : []).filter((r) => r && Number.isFinite(r.ms) && (!Number.isFinite(since) || r.ms >= since));
  return bpSessionWindow(after, Object.assign({}, opts || {}, { reportKind: 'quarterly' }));
}

// The rounded mean WF9 stores as the provisional baseline: what WF37 would file at this moment.
function bpSessionMean(readings) {
  const rows = Array.isArray(readings) ? readings : [];
  if (!rows.length) return null;
  const sum = rows.reduce((s, r) => ({ sys: s.sys + r.sys, dia: s.dia + r.dia }), { sys: 0, dia: 0 });
  return { sys: Math.round(sum.sys / rows.length), dia: Math.round(sum.dia / rows.length) };
}

// The evidence a closed ask carries, read back by WF10 to decide whether the readings are going stale.
function bpSessionEvidence(day) { return 'bp_session:' + String(day || ''); }
function bpSessionEvidenceDay(ref) {
  const m = /^bp_session:(\d{4}-\d{2}-\d{2})$/.exec(String(ref == null ? '' : ref));
  return m ? m[1] : null;
}
// BP-SESSION-END

export {
  BP_SESSION_WINDOW_DAYS, BP_SESSION_MIN_READINGS, BP_SESSION_DEFAULT_TZ, BP_SESSION_MANUAL_ATTRIB,
  BP_SESSION_RULE_BASELINE, BP_SESSION_RULE_LATEST,
  bpTimeZone, bpLocalClock, bpIsManualAttrib, bpPairsFromMeasures, bpSessionWindow, bpSessionSince,
  bpSessionMean, bpSessionEvidence, bpSessionEvidenceDay,
};
